import { useCallback, useEffect, useState } from "react";
import { useMap } from "react-map-gl/mapbox";
import type { PlaceType } from "../../../lib/api";
import { ADVENTURE_MAP_PLACES_LAYER_ID, PLACES_LAYER_ID, SELECTED_PLACE_LAYER_ID } from "../../../lib/mapConstants";
import { suggestPlaceType } from "../../../lib/mapboxUtils";

/** Layer IDs to exclude from POI click handling (our own place layers) */
const EXCLUDED_LAYER_IDS = [PLACES_LAYER_ID, ADVENTURE_MAP_PLACES_LAYER_ID, SELECTED_PLACE_LAYER_ID];

/** Layer ID patterns for clickable map labels (POIs, places, etc.) */
const CLICKABLE_LAYER_PATTERNS = ["poi", "place", "settlement", "country", "state", "natural", "water", "landuse"];

const isClickableLayer = (layerId: string) =>
  !layerId.includes("waterway") && CLICKABLE_LAYER_PATTERNS.some((p) => layerId.includes(p));

interface MapboxPoiClickEvent {
  /** Name of the POI (uses English name if available) */
  name: string;
  /** Coordinates of the POI */
  coordinates: [number, number];
  /** Suggested place type based on Maki icon */
  suggestedType: PlaceType | null;
}

interface MapboxPoiLayerProps {
  /** Whether POI click handling is enabled */
  enabled?: boolean;
  /** Called when a Mapbox POI is clicked */
  onClick?: (event: MapboxPoiClickEvent) => void;
  /** Called when a Mapbox POI is right-clicked / long-pressed */
  onContextMenu?: (event: MapboxPoiClickEvent) => void;
}

/**
 * Intercepts clicks on Mapbox POI labels (restaurants, parks, etc.)
 * and extracts place information.
 *
 * This is a virtual layer that doesn't render anything - it just
 * attaches click handlers to Mapbox's built-in label layers.
 */
export function MapboxPoiLayer({ enabled = true, onClick, onContextMenu }: MapboxPoiLayerProps) {
  const { current: map } = useMap();
  const [clickableLayers, setClickableLayers] = useState<string[]>([]);

  // Find clickable label layers in the style
  useEffect(() => {
    if (!map) return;

    const findClickableLayers = () => {
      const style = map.getStyle();
      if (!style?.layers) return;

      const layers = style.layers
        .filter((layer) => layer.type === "symbol" && isClickableLayer(layer.id))
        .map((layer) => layer.id);

      setClickableLayers(layers);
    };

    // Find layers once style is loaded
    if (map.isStyleLoaded()) {
      findClickableLayers();
    } else {
      map.once("styledata", findClickableLayers);
    }
  }, [map]);

  // Extract POI info from a map event
  const extractPoiInfo = useCallback(
    (e: mapboxgl.MapMouseEvent): MapboxPoiClickEvent | null => {
      if (!enabled || !map) return null;

      // Skip if we clicked on our own place layers
      const excludedFeatures = map.queryRenderedFeatures(e.point, {
        layers: EXCLUDED_LAYER_IDS.filter((id) => map.getLayer(id)),
      });
      if (excludedFeatures?.length) return null;

      const feature = e.features?.[0];
      if (!feature?.properties?.name) return null;

      // Use English name if available, fallback to default
      const name = (feature.properties.name_en || feature.properties.name) as string;
      const maki = feature.properties.maki as string | undefined;
      const poiClass = feature.properties.class as string | undefined;
      // Use POI geometry coordinates, not click coordinates
      const geometry = feature.geometry as GeoJSON.Point;
      const coordinates: [number, number] = geometry.coordinates as [number, number];
      const suggestedType = suggestPlaceType({ maki, poiClass });

      return { name, coordinates, suggestedType };
    },
    [map, enabled],
  );

  // Click handler
  const handleClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!onClick) return;
      const info = extractPoiInfo(e);
      if (info) {
        e.preventDefault();
        onClick(info);
      }
    },
    [extractPoiInfo, onClick],
  );

  // Context menu handler
  const handleContextMenu = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!onContextMenu) return;
      const info = extractPoiInfo(e);
      if (info) {
        e.preventDefault();
        onContextMenu(info);
      }
    },
    [extractPoiInfo, onContextMenu],
  );

  // Attach click handlers
  useEffect(() => {
    if (!map || clickableLayers.length === 0 || !enabled) return;

    clickableLayers.forEach((layerId) => {
      map.on("click", layerId, handleClick);
      map.on("contextmenu", layerId, handleContextMenu);
    });

    return () => {
      clickableLayers.forEach((layerId) => {
        map.off("click", layerId, handleClick);
        map.off("contextmenu", layerId, handleContextMenu);
      });
    };
  }, [map, clickableLayers, enabled, handleClick, handleContextMenu]);

  // Cursor feedback
  useEffect(() => {
    if (!map || clickableLayers.length === 0 || !enabled) return;

    const handleEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };

    const handleLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    clickableLayers.forEach((layerId) => {
      map.on("mouseenter", layerId, handleEnter);
      map.on("mouseleave", layerId, handleLeave);
    });

    return () => {
      clickableLayers.forEach((layerId) => {
        map.off("mouseenter", layerId, handleEnter);
        map.off("mouseleave", layerId, handleLeave);
      });
    };
  }, [map, clickableLayers, enabled]);

  // This is a virtual layer - nothing to render
  return null;
}
